import { useEffect, useState } from 'react'
import { useChainId } from 'wagmi'

import { useSpkFarmData } from '../../../data/useSpkFarmData'
import { calculateReward } from '../../../utils/calculateReward'
import { TxOverview } from '../types'
import { TokenWithValue } from '@/domain/common/types'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'
import { useTimestamp } from '@/utils/useTimestamp'

export interface UseClaimDialogResult {
  txOverview: TxOverview
  pageStatus: PageStatus
  actionsViewState: ActionsViewState
  reward: TokenWithValue
}

export function useClaimDialog(): UseClaimDialogResult {
  const chainId = useChainId()
  const { timestamp, updateTimestamp } = useTimestamp()
  useEffect(() => {
    updateTimestamp()
  }, [updateTimestamp])

  const { account } = useConnectedAddress()
  const tokenRepository = useSpkTokenRepository({ chainId, account })
  const spkFarmData = useSpkFarmData({ chainId, tokenRepository })

  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const rewardAmount = calculateReward({
    earned: spkFarmData.earned,
    staked: spkFarmData.staked,
    rewardRate: spkFarmData.rewardRate,
    earnedTimestamp: spkFarmData.earnedTimestamp,
    periodFinish: spkFarmData.periodFinish,
    timestampInMs: timestamp * 1000,
    totalSupply: spkFarmData.totalSupply,
  })

  const objectives = [
    {
      type: 'claimFarmRewards' as const,
      farm: spkFarmData.farmAddress,
      rewardToken: spkFarmData.rewardToken,
      rewardAmount,
    },
  ]

  const { viewState: actionsViewState } = useActions({
    objectives,
    context: {
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: true,
  })

  return {
    txOverview: {
      reward: {
        token: spkFarmData.rewardToken,
        value: rewardAmount,
      },
    },
    pageStatus: {
      state: pageStatus,
      actionsEnabled: true,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    actionsViewState,
    reward: {
      token: spkFarmData.rewardToken,
      value: rewardAmount,
    },
  }
}
